import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  section?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  correlationId: string | null;
}

/**
 * Section-level error boundary for admin pages.
 * Shows an inline error UI with retry instead of crashing the whole app.
 */
export class AdminErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, correlationId: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Try to extract correlation_id from error if it's an API error
    let correlationId: string | null = null;
    if (error && typeof error === "object" && "correlationId" in error) {
      correlationId = (error as { correlationId?: string }).correlationId ?? null;
    }
    return { hasError: true, error, correlationId };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(`[AdminErrorBoundary:${this.props.section ?? "unknown"}]`, {
      error: error.message,
      correlationId: this.state.correlationId,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, correlationId: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, correlationId } = this.state;

    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/10">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-foreground">
                Errore nel caricamento
              </h2>
              {this.props.section && (
                <p className="text-xs text-muted-foreground">
                  Sezione: {this.props.section}
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-md border border-red-500/20 bg-red-500/5 p-3">
              <p className="text-xs font-mono text-red-700 dark:text-red-400 break-all">
                {error.message}
              </p>
            </div>
          )}

          {correlationId && (
            <p className="text-xs text-muted-foreground mb-4">
              Correlation ID: <code className="font-mono">{correlationId}</code>
            </p>
          )}

          <button
            onClick={this.handleReset}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Riprova
          </button>
        </div>
      </div>
    );
  }
}
